import restDataProvider from "@providers/RestDataProvider";
import { ReactRouter, ReactRouterProvider } from "@providers";

import { useLocation, redirect } from "react-router-dom";

import axios from "axios";

import { LoadingOverlay, Global } from "@mantine/core";

import { useAuth0Provider } from "@components/auth/useAuth0Provider";
import { MyDealAuth0Provider } from "@providers/MyDealAuth0Provider";
import { TicketSummaryContextProvider } from "./pages/enquiryInbox";
import { ShippingSettingsContextProvider } from "@pages/shipping";
import { OnboardingStatusContextProvider } from "@pages/onboarding/onboardingStatusContext";
import { TestingConfigContextProvider } from "@utils/testingConfigContext";
import { IMyDealAppWrapperProps, MyDealMantine } from "@components";
import { layouts } from "./routes";
import { redirects } from "./routes/redirects";
import { useEffect } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { ErrorBoundary, ErrorBoundaryFallback } from "@datadog/browser-rum-react";

const AppLoading = () => {
  return <LoadingOverlay visible={true} />;
};

const OutletWrapper = ({ children }: any) => {
  const location = useLocation();

  useEffect(() => {
    datadogRum.startView({ name: location.pathname });
  }, [location.pathname]);

  return children;
};

const ErrorFallback: ErrorBoundaryFallback = ({ resetError, error }) => {
  return (
    <p>
      Oops, something went wrong! <strong>{String(error)}</strong> <button onClick={resetError}>Retry</button>
    </p>
  );
};

const AppWrapper: React.FC<IMyDealAppWrapperProps> = ({ children }) => {
  return (    
      <TestingConfigContextProvider>
        <OnboardingStatusContextProvider>
          <ShippingSettingsContextProvider>
            <TicketSummaryContextProvider>{children}</TicketSummaryContextProvider>
          </ShippingSettingsContextProvider>
        </OnboardingStatusContextProvider>
      </TestingConfigContextProvider>
  );
};

const trimTrailingSlash = (str: string) => {
  return str.endsWith("/") ? str.slice(0, -1) : str;
};

export const App = () => {
  const runtimeConfig = window?.__RUNTIME_CONFIG__ || {};

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      if (error.response) {
        if (error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          await redirect("/login");
          return error.response;
        }
      }
      return Promise.reject(error);
    }
  );

  return (
    <ErrorBoundary fallback={ErrorFallback}>
      <MyDealMantine
        routerProvider={ReactRouterProvider}
        router={
          <ReactRouter
            type="browser"
            useAuthProvider={useAuth0Provider}
            AuthProvider={MyDealAuth0Provider}
            LoadingComponent={AppLoading}
            dataProviders={{
              default: () =>
                restDataProvider(`${trimTrailingSlash(runtimeConfig.REACT_APP_API_BASE_URL)}/api/v1`, axios),
            }}
            routes={[...layouts, ...redirects]}
            outletWrapper={OutletWrapper}
          />
        }
        appWrapperComponent={AppWrapper}
      />
      <Global
        styles={{
          ".mydeal-Anchor-root:has(svg)": {
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          },
        }}
      />
    </ErrorBoundary>
  );
};

export default App;
